import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FolderIcon,
  FolderOpenIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentIcon,
  PhotoIcon,
  FilmIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  FolderPlusIcon,
  ArrowRightIcon,
  EyeIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { FolderIcon as FolderSolid } from '@heroicons/react/24/solid';

interface FileNode {
  id: string;
  name: string;
  type: 'folder' | 'file';
  children?: FileNode[];
  mediaItem?: any;
  path: string;
  expanded?: boolean;
  itemCount?: number;
}

interface FileBrowserProps {
  folders: any[];
  mediaItems: any[];
  selectedFolder: number | null;
  onFolderSelect: (folderId: number | null) => void;
  onFileSelect: (file: any) => void;
  onFolderCreate?: (parentId: number | null, name: string) => void;
  onFileMove?: (fileId: number, targetFolderId: number) => void;
  onFileDelete?: (fileId: number) => void;
  onFileRename?: (fileId: number, newName: string) => void;
  onFileDuplicate?: (fileId: number) => void;
}

interface ContextMenuProps {
  x: number;
  y: number;
  item: FileNode;
  onClose: () => void;
  onAction: (action: string, item: FileNode) => void;
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

interface PreviewTooltipProps {
  item: any;
  x: number;
  y: number;
}

// Context Menu Component
const ContextMenu: React.FC<ContextMenuProps> = (props) => {
  const { x, y, item, onClose, onAction } = props;
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const menuItems = item.type === 'folder' ? [
    { icon: FolderPlusIcon, label: 'New Folder', action: 'new-folder' },
    { icon: PencilIcon, label: 'Rename', action: 'rename' },
    { icon: TrashIcon, label: 'Delete', action: 'delete', className: 'text-red-600' },
  ] : [
    { icon: EyeIcon, label: 'Preview', action: 'preview' },
    { icon: PencilIcon, label: 'Rename', action: 'rename' },
    { icon: DocumentDuplicateIcon, label: 'Duplicate', action: 'duplicate' },
    { icon: ArrowDownTrayIcon, label: 'Download', action: 'download' },
    { icon: LinkIcon, label: 'Copy URL', action: 'copy-url' },
    { icon: ArrowRightIcon, label: 'Move to...', action: 'move' },
    { icon: TrashIcon, label: 'Delete', action: 'delete', className: 'text-red-600' },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50 min-w-[160px]"
      style={{ left: `${x}px`, top: `${y}px` }}
    >
      {menuItems.map((item, index) => {
        const Icon = item.icon;
        return (
          <button
            key={item.action}
            onClick={() => onAction(item.action, props.item)}
            className={`w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
              item.className || 'text-gray-700 dark:text-gray-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

// Preview Tooltip Component
const PreviewTooltip: React.FC<PreviewTooltipProps> = ({ item, x, y }) => {
  if (!item.mediaItem) return null;

  const { title, original_size, width, height, category, uploaded_at, thumbnail_path, optimized_path } = item.mediaItem;

  return (
    <div
      className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-50"
      style={{
        left: `${Math.min(x + 20, window.innerWidth - 350)}px`,
        top: `${Math.min(y - 150, window.innerHeight - 300)}px`,
        width: '320px'
      }}
    >
      {(thumbnail_path || optimized_path) && (
        <img
          src={thumbnail_path || optimized_path}
          alt={title}
          className="w-full h-48 object-cover rounded mb-3"
        />
      )}
      <div className="space-y-1 text-sm">
        <p className="font-medium text-gray-900 dark:text-white truncate">{title}</p>
        <p className="text-gray-500 dark:text-gray-400">
          {width} × {height} • {formatFileSize(original_size)}
        </p>
        <p className="text-gray-500 dark:text-gray-400">
          Category: {category} • {new Date(uploaded_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export default function FileBrowserEnhanced({
  folders,
  mediaItems,
  selectedFolder,
  onFolderSelect,
  onFileSelect,
  onFolderCreate,
  onFileMove,
  onFileDelete,
  onFileRename,
  onFileDuplicate
}: FileBrowserProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['root']));
  const [fileTree, setFileTree] = useState<FileNode | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [lastSelectedItem, setLastSelectedItem] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: FileNode } | null>(null);
  const [previewItem, setPreviewItem] = useState<{ item: FileNode; x: number; y: number } | null>(null);
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<FileNode[]>([]);
  const [renameItem, setRenameItem] = useState<string | null>(null);
  const [renameName, setRenameName] = useState<string>('');
  const previewTimeoutRef = useRef<NodeJS.Timeout>();

  // Build file tree from folders and media items
  useEffect(() => {
    const buildTree = () => {
      const root: FileNode = {
        id: 'root',
        name: 'Home',
        type: 'folder',
        path: '/',
        children: [],
        expanded: true
      };

      const folderMap = new Map<number, FileNode>();

      // Build all folder nodes from database
      folders.forEach(folder => {
        const node: FileNode = {
          id: `folder-${folder.id}`,
          name: folder.name,
          type: 'folder',
          path: folder.path || folder.name,
          children: [],
          itemCount: folder.item_count
        };
        folderMap.set(folder.id, node);
      });

      // Build folder hierarchy
      folders.forEach(folder => {
        const node = folderMap.get(folder.id);
        if (node) {
          if (folder.parent_id && folderMap.has(folder.parent_id)) {
            // Add to parent folder
            const parent = folderMap.get(folder.parent_id);
            parent?.children?.push(node);
          } else {
            // Add to root (Home) if no parent
            root.children?.push(node);
          }
        }
      });

      const uncategorizedFiles = mediaItems.filter(item => !item.folder_id);
      uncategorizedFiles.forEach(item => {
        const fileNode: FileNode = {
          id: `file-${item.id}`,
          name: item.title || item.filename,
          type: 'file',
          path: item.title || item.filename,
          mediaItem: item
        };
        root.children?.push(fileNode);
      });

      mediaItems.forEach(item => {
        if (item.folder_id && folderMap.has(item.folder_id)) {
          const folder = folderMap.get(item.folder_id);
          const fileNode: FileNode = {
            id: `file-${item.id}`,
            name: item.title || item.filename,
            type: 'file',
            path: `${folder?.path}/${item.title || item.filename}`,
            mediaItem: item
          };
          folder?.children?.push(fileNode);
        }
      });

      const sortChildren = (node: FileNode) => {
        if (node.children) {
          node.children.sort((a, b) => {
            if (a.type === 'folder' && b.type === 'file') return -1;
            if (a.type === 'file' && b.type === 'folder') return 1;
            return a.name.localeCompare(b.name);
          });
          node.children.forEach(child => {
            if (child.type === 'folder') sortChildren(child);
          });
        }
      };
      sortChildren(root);

      setFileTree(root);
    };

    buildTree();
  }, [folders, mediaItems]);

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleItemClick = (node: FileNode, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.shiftKey && lastSelectedItem) {
      // Shift+click: Select range
      selectRange(lastSelectedItem, node.id);
    } else if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd+click: Toggle selection
      toggleSelection(node.id);
    } else {
      // Regular click: Select single item
      setSelectedItems(new Set([node.id]));
      setLastSelectedItem(node.id);

      if (node.type === 'folder') {
        toggleFolder(node.id);
        if (node.id === 'root') {
          onFolderSelect(null);
        } else {
          const folderId = parseInt(node.id.replace('folder-', ''));
          onFolderSelect(folderId);
        }
      } else {
        onFileSelect(node.mediaItem);
      }
    }
  };

  const toggleSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
    setLastSelectedItem(itemId);
  };

  const selectRange = (fromId: string, toId: string) => {
    // Implement range selection logic
    // This is simplified - you'd need to traverse the tree to find items between fromId and toId
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.add(fromId);
      newSet.add(toId);
      return newSet;
    });
  };

  const handleContextMenu = (e: React.MouseEvent, node: FileNode) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, item: node });
  };

  const handleContextMenuAction = (action: string, item: FileNode) => {
    const fileId = item.id.startsWith('file-') ? parseInt(item.id.replace('file-', '')) : null;

    switch (action) {
      case 'preview':
        if (item.mediaItem) onFileSelect(item.mediaItem);
        break;
      case 'rename':
        setRenameItem(item.id);
        setRenameName(item.name);
        break;
      case 'delete':
        if (fileId && onFileDelete) {
          if (confirm(`Delete ${item.name}?`)) {
            onFileDelete(fileId);
          }
        }
        break;
      case 'duplicate':
        if (fileId && onFileDuplicate) {
          onFileDuplicate(fileId);
        }
        break;
      case 'download':
        if (item.mediaItem) {
          window.open(item.mediaItem.original_path, '_blank');
        }
        break;
      case 'copy-url':
        if (item.mediaItem) {
          navigator.clipboard.writeText(window.location.origin + item.mediaItem.original_path);
        }
        break;
      case 'move':
        if (fileId && onFileMove) {
          // For now, use a simple prompt. Will be replaced with FolderSelector
          const targetFolderName = prompt('Enter target folder name (or leave empty for root):');
          if (targetFolderName !== null) {
            // Find folder by name
            const findFolderByName = (node: FileNode, name: string): FileNode | null => {
              if (node.type === 'folder' && node.name.toLowerCase() === name.toLowerCase()) {
                return node;
              }
              if (node.children) {
                for (const child of node.children) {
                  const found = findFolderByName(child, name);
                  if (found) return found;
                }
              }
              return null;
            };

            if (targetFolderName === '') {
              // Move to root
              onFileMove(fileId, 0);
            } else if (fileTree) {
              const targetFolder = findFolderByName(fileTree, targetFolderName);
              if (targetFolder && targetFolder.id !== 'root') {
                const folderId = parseInt(targetFolder.id.replace('folder-', ''));
                onFileMove(fileId, folderId);
              } else {
                alert('Folder not found');
              }
            }
          }
        }
        break;
      case 'new-folder':
        const name = prompt('Folder name:');
        if (name && onFolderCreate) {
          const parentId = item.id === 'root' ? null : parseInt(item.id.replace('folder-', ''));
          onFolderCreate(parentId, name);
        }
        break;
    }
    setContextMenu(null);
  };

  const handleMouseEnter = (e: React.MouseEvent, node: FileNode) => {
    if (node.type === 'file' && node.mediaItem) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = setTimeout(() => {
        setPreviewItem({ item: node, x: e.clientX, y: e.clientY });
      }, 500);
    }
  };

  const handleMouseLeave = () => {
    clearTimeout(previewTimeoutRef.current);
    setPreviewItem(null);
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setSelectedItems(new Set());
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
      e.preventDefault();
      // Select all items
      const allIds = new Set<string>();
      const collectIds = (node: FileNode) => {
        if (node.id !== 'root') allIds.add(node.id);
        node.children?.forEach(collectIds);
      };
      if (fileTree) collectIds(fileTree);
      setSelectedItems(allIds);
    }
  }, [fileTree]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFileIcon = (item: FileNode) => {
    if (item.type === 'folder') {
      return expandedFolders.has(item.id) ? FolderOpenIcon : FolderIcon;
    }

    const extension = item.name.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) {
      return PhotoIcon;
    }
    if (['mp4', 'avi', 'mov', 'webm'].includes(extension || '')) {
      return FilmIcon;
    }
    return DocumentIcon;
  };

  const renderNode = (node: FileNode, level: number = 0) => {
    const isExpanded = expandedFolders.has(node.id);
    const Icon = getFileIcon(node);
    const isSelected = selectedItems.has(node.id);
    const isDragOver = dragOverFolder === node.id;
    const isRenaming = renameItem === node.id;

    return (
      <div key={node.id} className="select-none">
        <div
          className={`
            flex items-center px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer
            ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''}
            ${isDragOver ? 'bg-blue-100 dark:bg-blue-800/40 border-2 border-blue-400' : ''}
          `}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={(e) => handleItemClick(node, e)}
          onContextMenu={(e) => handleContextMenu(e, node)}
          onMouseEnter={(e) => handleMouseEnter(e, node)}
          onMouseLeave={handleMouseLeave}
          draggable={node.type === 'file'}
        >
          {node.type === 'folder' && node.children && node.children.length > 0 && (
            <span className="mr-1">
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-500" />
              )}
            </span>
          )}
          {node.type === 'folder' && (!node.children || node.children.length === 0) && (
            <span className="mr-1 w-4" />
          )}

          <Icon className={`w-5 h-5 mr-2 ${node.type === 'folder' ? 'text-blue-500' : 'text-gray-400'}`} />

          {isRenaming ? (
            <input
              type="text"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onBlur={() => {
                if (renameName && renameName !== node.name) {
                  const fileId = parseInt(node.id.replace('file-', ''));
                  if (onFileRename) onFileRename(fileId, renameName);
                }
                setRenameItem(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  setRenameItem(null);
                }
              }}
              className="flex-1 px-1 text-sm bg-white dark:bg-gray-700 border border-blue-500 rounded"
              autoFocus
            />
          ) : (
            <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">
              {node.name}
            </span>
          )}

          {node.type === 'folder' && node.itemCount !== undefined && (
            <span className="text-xs text-gray-500 ml-2">
              {node.itemCount}
            </span>
          )}
        </div>

        {node.type === 'folder' && isExpanded && node.children && (
          <div>
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with New Folder button */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => {
            const name = prompt('Folder name:');
            if (name && onFolderCreate) {
              onFolderCreate(null, name);
            }
          }}
          className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FolderPlusIcon className="w-4 h-4 mr-2" />
          New Folder
        </button>
      </div>

      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center text-sm">
            <button
              onClick={() => {
                onFolderSelect(null);
                setBreadcrumbs([]);
              }}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              All Files
            </button>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id}>
                <ChevronRightIcon className="w-4 h-4 mx-1 text-gray-400" />
                <button
                  className={`
                    ${index === breadcrumbs.length - 1
                      ? 'text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }
                  `}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* File Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        {fileTree && renderNode(fileTree)}
      </div>

      {/* Storage Info */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div className="text-xs text-gray-500">
          <div className="flex justify-between mb-1">
            <span>Storage Used</span>
            <span className="font-medium">6 GB / 60 GB</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full"
              style={{ width: '10%' }}
            />
          </div>
        </div>
        {selectedItems.size > 0 && (
          <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
            {selectedItems.size} items selected
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          item={contextMenu.item}
          onClose={() => setContextMenu(null)}
          onAction={handleContextMenuAction}
        />
      )}

      {/* Preview Tooltip */}
      {previewItem && (
        <PreviewTooltip
          item={previewItem.item}
          x={previewItem.x}
          y={previewItem.y}
        />
      )}
    </div>
  );
}